import { checkQuadraticExpression } from "../../../../utils/quadraticExpression";
﻿// src/generators/dzial3_funkcje/temat3_kwadratowa/podtemat2_kanoniczna_types/TransformCanonicalToGeneralTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

/* =============================================================
   TASK: TransformCanonicalToGeneralTask
   Z postaci kanonicznej f(x)=a(x-p)^2+q zapisz postać ogólną f(x)=ax^2+bx+c.
   Użytkownik wpisuje tylko prawą stronę; UI pokazuje prefiks "f(x) =".
============================================================= */

/* ---------- helpers: liczby, frakcje, latex ---------- */

function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do { v = Math.floor(Math.random() * span) + min; } while (exclude.has(v));
  return v;
}

function gcd(a, b){ a=Math.abs(a); b=Math.abs(b); while(b){const t=a%b;a=b;b=t;} return a||1; }
function simplifyFrac(n, d){
  if (d === 0) return { n, d };
  const s = Math.sign(n)*Math.sign(d) || 1;
  const an = Math.abs(n), ad = Math.abs(d);
  const g = gcd(an, ad);
  return { n: s*(an/g), d: ad/g };
}
function addFrac(a, b){ return simplifyFrac(a.n*b.d + b.n*a.d, a.d*b.d); }
function mulFrac(a, b){ return simplifyFrac(a.n*b.n, a.d*b.d); }
function squareFrac(a){ return simplifyFrac(a.n*a.n, a.d*a.d); }
function intToFrac(k){ return { n: k, d: 1 }; }

function formatFracMixed(n, d){
  const sgn = Math.sign(n) < 0 ? "-" : "";
  const N = Math.abs(n), D = Math.abs(d);
  if (N % D === 0) return `${sgn}${N/D}`;
  const whole = Math.floor(N/D);
  const rem = N % D;
  if (whole === 0) return `${sgn}\\dfrac{${N}}{${D}}`;
  return `${sgn}${whole}\\dfrac{${rem}}{${D}}`;
}
function formatFracND(n, d){
  if (d === 1) return `${n}`;
  const sgn = Math.sign(n) < 0 ? "-" : "";
  return `${sgn}\\dfrac{${Math.abs(n)}}{${Math.abs(d)}}`;
}

const par = (v) => (v < 0 ? `(${v})` : `${v}`);

/* ---------- LaTeX postaci kanonicznej i ogólnej ---------- */

function canonicalLatex(a, pFrac, qFrac) {
  const aLatex = Math.abs(a) === 1 ? (a === -1 ? "-" : "") : String(a);

  const pStr = formatFracMixed(pFrac.n, pFrac.d);
  const pIsNeg = pFrac.n < 0;
  const pAbsStr = formatFracMixed(Math.abs(pFrac.n), pFrac.d);

  const qStr = formatFracMixed(qFrac.n, qFrac.d);
  const qIsNeg = qFrac.n < 0;
  const qAbsStr = formatFracMixed(Math.abs(qFrac.n), qFrac.d);

  const xMinusP = pIsNeg ? `x + ${pAbsStr}` : `x - ${pStr}`;
  const plusMinusQ = qIsNeg ? ` - ${qAbsStr}` : ` + ${qStr}`;
  return `${aLatex}\\big(${xMinusP}\\big)^{2}${plusMinusQ}`;
}

function generalLatex(a, b, c) {
  const aStr = (a === 1 ? "" : (a === -1 ? "-" : String(a)));
  const bStr = b === 0 ? "" : (b > 0 ? ` + ${Math.abs(b) === 1 ? "" : Math.abs(b)}x` : ` - ${Math.abs(b) === 1 ? "" : Math.abs(b)}x`);
  const cStr = c === 0 ? "" : (c > 0 ? ` + ${Math.abs(c)}` : ` - ${Math.abs(c)}`);
  return `${aStr}x^2${bStr}${cStr}` || "0";
}

/* ---------- surowy tekst z EquationInput + normalizacja ---------- */

function getRaw(val) {
  if (typeof val === "string") return val;
  if (val && typeof val === "object") {
    for (const k of ["raw","value","text","input","expr","equation","latex"]) {
      const v = val[k];
      if (typeof v === "string" && v.trim() !== "") return v;
    }
    try {
      const s = String(val);
      if (s && s !== "[object Object]") return s;
    } catch {}
  }
  return "";
}



// „ + 1 1/6 ” → „ + 7/6 ”; zachowujemy znak




/* ---------- podgląd (ładne frakcje bez ruszania treści) ---------- */

function toLatexFractionSafe(str) {
  if (!str) return "";
  let s = str.replace(/([+\-]?)\s*(\d+)\s+(\d+)\s*\/\s*(\d+)/g,
    (_, sign, whole, num, den) => `${sign}${whole}\\dfrac{${num}}{${den}}`
  );
  s = s.replace(/(?<!\\dfrac\{)(\d+)\/(\d+)/g, (_, num, den) => `\\dfrac{${num}}{${den}}`);
  return s;
}

/* ---------- ewaluacja numeryczna (fallback) ---------- */








/* ---------- PROSTE parsowanie ax^2+bx+c (jeśli się uda) ---------- */







/* ---------- generator ---------- */

export function generateTask() {
  // Unikamy trywiałów: a≠0, b≠0, c≠0 ⇒ p=-b/(2a) ≠ 0 i ogólna ≠ kanoniczna
  const a = randInt(-5, 5, new Set([0]));
  const b = randInt(-9, 9, new Set([0]));
  const c = randInt(-9, 9, new Set([0]));

  const delta = b*b - 4*a*c;
  const pFrac = simplifyFrac(-b, 2*a);
  const qFrac = simplifyFrac(-delta, 4*a);

  return {
    type: "TransformCanonicalToGeneralTask",
    data: {
      a, b, c,
      delta, pFrac, qFrac,
      p: pFrac.n / pFrac.d,
      q: qFrac.n / qFrac.d,
    }
  };
}

/* ---------- render ---------- */

export function renderTask({ data }) {
  const { a, pFrac, qFrac } = data;
  const fxCan = canonicalLatex(a, pFrac, qFrac);

  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <div>
        Dana jest funkcja w postaci kanonicznej: <InlineMath math={`f(x) = ${fxCan}`} />
      </div>
      <div>
        Zapisz ją w postaci ogólnej <InlineMath math={'f(x) = ax^2 + bx + c'} />.
      </div>
    </div>
  );
}

// INPUT: prefiks f(x)=; podgląd dopiero gdy coś wpiszesz; pokazujemy surowy zapis (ładny LaTeX)
export function renderInput(_task, userAnswer, onChange, disabled) {
  const raw = getRaw(userAnswer);
  const hasText = raw.trim().length > 0;

  return (
    <div style={{ display: "grid", gap: 8, marginTop: 6 }}>
      <div><InlineMath math={'f(x) ='} /></div>
      <EquationInput
        value={raw}
        onChange={(val) => onChange(val)}
        disabled={disabled}
        placeholder="np. 2x^2 - 3x + 5"
        showPreview={false}
      />
      {hasText && (
        <div style={{ opacity: 0.85 }}>
          <span style={{ marginRight: 6 }}>Podgląd:</span>
          <InlineMath math={`f(x) = ${toLatexFractionSafe(raw)}`} />
        </div>
      )}
    </div>
  );
}

/* ---------- check + answers ---------- */

export function checkAnswer(arg1, arg2) {
  const task = arg1 && arg1.data ? arg1 : arg2;
  const userAnswer = arg1 && arg1.data ? arg2 : arg1;
  return checkQuadraticExpression(getRaw(userAnswer), task.data, { form: "general" });
}

export function renderUserAnswer(arg1, arg2) {
  const looksLikeTask = (x) => x && typeof x === "object" && x.data;
  const userAnswer = looksLikeTask(arg1) ? arg2 : arg1;

  const raw = getRaw(userAnswer);
  if (!raw || !raw.trim()) return <i>(brak odpowiedzi)</i>;

  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <InlineMath math={`f(x) = ${toLatexFractionSafe(raw)}`} />
    </div>
  );
}

export function renderAnswer({ data }) {
  const { a, b, c } = data;
  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <InlineMath math={`f(x) = ${generalLatex(a, b, c)}`} />
    </div>
  );
}

/* ---------- explanation: kroki z warunkowym skracaniem ---------- */
export function renderExplanation({ data }) {
  const { a, b, c, pFrac, qFrac } = data;

  // LaTeX pomocniczy
  const fracND = (n,d) => (d === 1 ? `${n}` : `\\dfrac{${n}}{${d}}`);
  const signedFrac = (n,d) =>
    n >= 0 ? fracND(n,d) : `-\\dfrac{${Math.abs(n)}}{${d}}`;

  const asCoefX = (n,d) => {
    // format współczynnika *x w linii „przed mnożeniem przez a”
    const s = n < 0 ? "-" : "+";
    const N = Math.abs(n), D = Math.abs(d);
    if (D === 1 && N === 1) return ` ${s} x`;
    const val = D === 1 ? `${N}` : `\\dfrac{${N}}{${D}}`;
    return ` ${s} ${val}x`;
  };
  const asCoefXInt = (K) => {
    // format współczynnika *x po wymnożeniu (już całkowity)
    if (K === 1)  return " + x";
    if (K === -1) return " - x";
    return K > 0 ? ` + ${K}x` : ` - ${Math.abs(K)}x`;
  };
  const leadA = (A) => (A === 1 ? "" : (A === -1 ? "-" : String(A)));
  const plusInt = (K) => (K >= 0 ? ` + ${K}` : ` - ${Math.abs(K)}`);

  // postaci p i q
  const pMixed = formatFracMixed(pFrac.n, pFrac.d);     // np. 6 3/4
  const pImp   = signedFrac(pFrac.n, pFrac.d);          // np. -5/2
  const qMixed = formatFracMixed(qFrac.n, qFrac.d);
  const qImp   = signedFrac(qFrac.n, qFrac.d);

  // KROK 1: pokazuj zamianę tylko gdy rzeczywiście „mieszana → niewłaściwy”
  const showPChange = pMixed !== pImp;
  const showQChange = qMixed !== qImp;

  // jak pisać p w produkcie -2·p·x (nawias tylko dla ujemnego p)
  const pInProduct = pFrac.n < 0
    ? `\\left(${pImp}\\right)`
    : (pFrac.d === 1 ? `${pFrac.n}` : fracND(pFrac.n, pFrac.d));

  // jak pisać p w potędze (nawias dla ułamka lub ujemnego)
  const pInPower = (pFrac.d === 1 && pFrac.n >= 0)
    ? `${pFrac.n}^{2}`
    : `\\left(${pImp}\\right)^{2}`;

  // ułamki pośrednie
  const m2p = simplifyFrac(-2 * pFrac.n, pFrac.d);            // -2p
  const p2  = simplifyFrac(pFrac.n * pFrac.n, pFrac.d*pFrac.d); // p^2
  const ap2 = simplifyFrac(a * p2.n, p2.d);                    // a*p^2
  const cSumN = ap2.n + qFrac.n;                               // suma liczników
  const cSumD = ap2.d;                                         // wspólny mianownik (gdy jest)

  // budujemy ciąg linii (warunkowo)
  const lines = [];

  // Krok 1
  const step1P = showPChange ? `p = ${pMixed} = ${pImp}` : `p = ${pImp}`;
  const step1Q = showQChange ? `q = ${qMixed} = ${qImp}` : `q = ${qImp}`;

  // Krok 2 – linia startowa
  lines.push(`f(x) = ${canonicalLatex(a, pFrac, qFrac)}`);

  // rozwinięcie wg wzoru (z właściwymi nawiasami i kropkami mnożenia)
  if (a === 1) {
    lines.push(`= x^2 - 2\\cdot ${pInProduct}\\,x + ${pInPower} + ${qImp}`);
  } else {
    lines.push(`= ${leadA(a)}\\big(x^2 - 2\\cdot ${pInProduct}\\,x + ${pInPower}\\big) + ${qImp}`);
  }

  // wstaw wartości -2·p oraz p^2 (bez mnożenia przez a)
  const m2pStr = signedFrac(m2p.n, m2p.d);   // może być ±1, ±ułamek
  const p2Str  = signedFrac(p2.n, p2.d);     // zawsze ≥0 (ale trzymamy format)

  if (a === 1) {
    // = x^2 + (±…)x + (…)
    lines.push(`= x^2${asCoefX(m2p.n, m2p.d)} + ${fracND(p2.n, p2.d)} + ${qImp}`);
  } else {
    lines.push(`= ${leadA(a)}\\big(x^2 ${m2p.n<0?'-':'+'} ${fracND(Math.abs(m2p.n), m2p.d)}x + ${fracND(p2.n, p2.d)}\\big) + ${qImp}`);
  }

  // wymnożenie przez a (jeśli a != 1)
  if (a !== 1) {
    lines.push(
      `= ${leadA(a)}x^2${asCoefXInt(b)}${(ap2.n>=0 ? ' + ' : ' - ')}${fracND(Math.abs(ap2.n), ap2.d)} + ${qImp}`
    );
  }

  // zsumuj stałe: ap^2 + q
  const showFractionSumLine = (ap2.d === qFrac.d); // najładniej, gdy wspólny mianownik
  if (a !== 1) {
    if (showFractionSumLine && c !== 0) {
      // np. +25/4 + 3/4 = +28/4
      lines.push(
        `= ${leadA(a)}x^2${asCoefXInt(b)} + ${fracND(ap2.n, ap2.d)} + ${qImp}`
      );
      const signSum = cSumN >= 0 ? " + " : " - ";
      lines.push(
        `= ${leadA(a)}x^2${asCoefXInt(b)}${signSum}${fracND(Math.abs(cSumN), cSumD)}`
      );
    }
    // ostatnia linia ze stałą już całkowitą
    lines.push(
      `= ${leadA(a)}x^2${asCoefXInt(b)}${plusInt(c)}`
    );
  } else {
    // a === 1
    // zsumuj p^2 + q
    const p2plusQn = p2.n + qFrac.n;
    const p2plusQd = p2.d; // ten sam mianownik co q przy naszych danych
    if (p2.d === qFrac.d && c !== 0) {
      lines.push(`= x^2${asCoefX(m2p.n, m2p.d)} + ${fracND(p2.n, p2.d)} + ${qImp}`);
      const signSum = p2plusQn >= 0 ? " + " : " - ";
      lines.push(`= x^2${asCoefX(m2p.n, m2p.d)}${signSum}${fracND(Math.abs(p2plusQn), p2plusQd)}`);
    }
    // zamień współczynnik przy x na postać całkowitą (b) i pokaż c
    lines.push(`= x^2${asCoefXInt(b)}${plusInt(c)}`);
  }

  // zlep w jeden aligned
  const aligned = lines.join(`\\\\[4pt] &`);

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, marginTop: 8 }}>
      <div><strong>Krok 1.</strong> Jeśli masz liczbę mieszaną – zamień ją na ułamek niewłaściwy:</div>
      <BlockMath math={`${step1P},\\quad ${step1Q}`} />

      <div style={{ marginTop: 6 }}>
        <strong>Krok 2.</strong> Obliczenia (jednym ciągiem, wg wzoru skróconego mnożenia):
      </div>
      <BlockMath math={`\\begin{aligned} &${aligned}\\end{aligned}`} />

      <div style={{ marginTop: 6 }}>
        <strong>Krok 3.</strong> Wynik w postaci ogólnej:
      </div>
      <BlockMath math={`f(x) = ${generalLatex(a, b, c)}`} />
    </div>
  );
}



export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderUserAnswer,
  renderAnswer,
  renderExplanation,
};
